import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import omnicareLogo from '../assets/icons/logo.svg';
import {
  CheckCircle2, ChevronDown, ChevronUp, Share2,
  ShieldCheck, BookOpen, ArrowRight, Activity,
  Heart, Download, FileText,
  Sparkles, Camera, Users, X, Twitter,
  Loader2, Image as ImageIcon
} from 'lucide-react';

// ─── Utility loaders ───
const loadHtml2Canvas = () => new Promise((resolve, reject) => {
  if (window.html2canvas) { resolve(window.html2canvas); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
  s.onload = () => resolve(window.html2canvas);
  s.onerror = () => reject(new Error('Failed to load html2canvas'));
  document.body.appendChild(s);
});
const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  s.onload = () => resolve(window.jspdf.jsPDF);
  s.onerror = () => reject(new Error('Failed to load jsPDF'));
  document.body.appendChild(s);
});

// ─── Cancer copy ───
const CANCER_COPY = {
  lung: { elevated: "A low-dose CT scan is quick, painless, and catches issues when they're most treatable.", routine: "No immediate screening needed. If you ever become a long-term smoker, ask about low-dose CT.", action: { elevated: "Ask about a low-dose CT scan", routine: "Learn about lung screening" } },
  breast: { elevated: "Mammograms are one of the most effective screening tools — and they only take about 20 minutes.", routine: "Regular mammograms at the recommended age are simple, fast, and genuinely lifesaving.", action: { elevated: "Schedule a mammogram", routine: "Check your mammogram timeline" } },
  cervical: { elevated: "A Pap test with HPV co-testing is the gold standard.", routine: "Cervical cancer is one of the most preventable cancers. Stay on top of routine Pap + HPV tests.", action: { elevated: "Book your Pap + HPV test", routine: "Confirm you're up to date" } },
  colorectal: { elevated: "Multiple options — from at-home stool tests to colonoscopy.", routine: "Screening starts at age 45 for most people.", action: { elevated: "Discuss early screening options", routine: "Plan your first screening" } },
  gastric: { elevated: "Proactive screening could make a real difference. An upper endoscopy is the most common first step.", routine: "If you develop persistent digestive symptoms, bring it up with your doctor.", action: { elevated: "Ask about an endoscopy", routine: "Know the warning signs" } },
  liver: { elevated: "Regular monitoring with ultrasound and blood tests (AFP) can catch changes early.", routine: "Healthy weight, limited alcohol, and up-to-date hepatitis vaccines are your best steps.", action: { elevated: "Ask about liver monitoring", routine: "Review your hepatitis status" } },
  thyroid: { elevated: "Highly treatable — a simple neck exam and ultrasound can catch most issues early.", routine: "Routine physical exams with a neck check are usually sufficient.", action: { elevated: "Request a thyroid check", routine: "Stay aware during checkups" } },
  skin: { elevated: "Annual full-body skin exams plus regular self-checks are your best defense.", routine: "Wear sunscreen, do monthly self-checks for new or changing moles.", action: { elevated: "Book a skin exam", routine: "Start monthly self-checks" } },
  prostate: { elevated: "A simple PSA blood test is the first step.", routine: "The PSA test is standard; talk to your doctor about when to start.", action: { elevated: "Ask about a PSA test", routine: "Plan your PSA timeline" } },
  ovarian: { elevated: "CA-125 blood tests and transvaginal ultrasound can help — especially with family history.", routine: "No routine screening for average risk. Report persistent symptoms.", action: { elevated: "Discuss monitoring options", routine: "Know the symptoms" } },
  uterine: { elevated: "Report any unexpected vaginal bleeding promptly.", routine: "Stay attentive to any unusual bleeding.", action: { elevated: "Talk to your gynecologist", routine: "Stay up to date on exams" } },
  testicular: { elevated: "One of the most treatable cancers when caught early.", routine: "Self-exams are simple and effective.", action: { elevated: "Learn the self-exam technique", routine: "Start monthly self-exams" } },
};
const DEFAULT_COPY = { elevated: "Early detection is powerful. Discuss screening with your doctor.", routine: "Staying on top of routine screenings is the best way to keep things on track.", action: { elevated: "Talk to your doctor", routine: "Plan your screening" } };

// ─── Helpers (copied from V2) ───
function mapRecommendationsToCards(recommendations, surveyData) {
  const { stronglyRecommend = [], recommend = [] } = recommendations || {};
  const allItems = [...stronglyRecommend, ...recommend];
  const iconMap = { gastric: Activity, liver: Activity, lung: ShieldCheck, breast: Heart, cervical: ShieldCheck, colorectal: Activity, thyroid: ShieldCheck, skin: ShieldCheck, prostate: ShieldCheck, ovarian: Heart, uterine: Heart, testicular: ShieldCheck };
  return allItems.map((item) => {
    const score = item.riskScore || 1.0;
    const isElevated = score > 1.2;
    let statusColor = 'green', priority = 'On Your Radar';
    if (score >= 2.5) { statusColor = 'red'; priority = 'Act Now'; }
    else if (score >= 1.5) { statusColor = 'orange'; priority = 'Worth a Conversation'; }
    else if (score > 1.2) { statusColor = 'stone'; priority = 'Good to Know'; }
    const riskFactors = item.riskFactors || [];
    const screening = item.screening || {};
    const copy = CANCER_COPY[item.cancerKey] || DEFAULT_COPY;
    const sources = [];
    if (item.guidelineSource?.org) sources.push(`${item.guidelineSource.org}${item.guidelineSource.note ? ': ' + item.guidelineSource.note : ''}`);
    if (screening.source) sources.push(screening.source);
    if (screening.guideline && !sources.length) sources.push(screening.guideline);
    const clinicalParts = [];
    if (riskFactors.length > 0) { clinicalParts.push("Profile findings:"); riskFactors.forEach(r => { clinicalParts.push(`• ${r.factor || r}${r.detail ? ' — ' + r.detail : ''}`); }); }
    if (screening.method) { clinicalParts.push(''); clinicalParts.push(`Screening: ${screening.method}`); if (screening.interval) clinicalParts.push(`Interval: ${screening.interval}`); if (screening.who) clinicalParts.push(`Who: ${screening.who}`); }
    if (screening.highRisk) { clinicalParts.push(''); clinicalParts.push(`High-risk: ${screening.highRisk}`); }
    if (item.statistics?.length > 0) { clinicalParts.push(''); item.statistics.forEach(s => clinicalParts.push(`• ${s.label}: ${s.value}`)); }
    return { title: item.cancer || 'Screening', priority, multiplier: score.toFixed(1), statusColor, icon: iconMap[item.cancerKey] || ShieldCheck, summary: isElevated ? copy.elevated : copy.routine, action: isElevated ? copy.action.elevated : copy.action.routine, clinicalText: clinicalParts.join('\n'), sources, isGovRecommended: item.tier === 'primary', guidelineSource: item.guidelineSource || null };
  });
}

function getArchetype(surveyData) {
  if (!surveyData) return { name: "The Seedling", emoji: "🌱", superpower: "Taking the first step", watchOut: "The unknown", percentile: "Top 60%" };
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits, familyCancerHistory = [], livedWithSmoker } = surveyData;
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  const isHealthyBmi = bmi && bmi >= 18.5 && bmi < 25;
  const isNonSmoker = (!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const isLowAlcohol = !alcoholConsumption || alcoholConsumption === "none" || alcoholConsumption === "under_7";
  const hasGoodDiet = !dietHabits || dietHabits === 'no' || dietHabits === 'unsure';
  const noSecondhandSmoke = livedWithSmoker !== 'yes';
  const healthScore = [isHealthyBmi, isNonSmoker, isLowAlcohol, hasGoodDiet, noSecondhandSmoke].filter(Boolean).length;
  const isFormerSmoker = (smokingHistory > 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const filteredFamily = (Array.isArray(familyCancerHistory) ? familyCancerHistory : []).filter(fh => fh.toLowerCase() !== 'none');
  const hasFamilyHistory = filteredFamily.length > 0;
  const alcoholIsWeakSpot = !isLowAlcohol;
  const dietIsWeakSpot = !hasGoodDiet;
  const weakSpot = !isNonSmoker ? "Smoking exposure" : !isLowAlcohol ? "Alcohol intake" : !hasGoodDiet ? "Processed food habits" : !isHealthyBmi ? "Weight management" : !noSecondhandSmoke ? "Secondhand smoke exposure" : hasFamilyHistory ? "Family history" : "Complacency";
  if (isFormerSmoker && healthScore >= 3) return { name: "The Phoenix", emoji: "🔥", superpower: "You made the hardest change already", watchOut: weakSpot, percentile: "Top 15%", tagline: "Rose from the ashes" };
  if (healthScore === 5 && !hasFamilyHistory) return { name: "The Zen Master", emoji: "🧘", superpower: "Balance in all things", watchOut: weakSpot, percentile: "Top 8%", tagline: "Calm, in control" };
  if (healthScore === 5 && hasFamilyHistory) return { name: "The Sentinel", emoji: "🛡️", superpower: "Proactive despite the odds", watchOut: weakSpot, percentile: "Top 10%", tagline: "Guarding what you can't control" };
  if (healthScore === 4) return { name: "The Adventurer", emoji: "🧭", superpower: "Mostly nailing it", watchOut: weakSpot, percentile: "Top 20%", tagline: "Mostly solid, one indulgence" };
  if (healthScore >= 2) {
    if (alcoholIsWeakSpot || dietIsWeakSpot) return { name: "The Rebel", emoji: "🎸", superpower: "You own the risks", watchOut: weakSpot, percentile: "Top 35%", tagline: "Lives a little, knows the score" };
    return { name: "The Night Owl", emoji: "🦉", superpower: "Self-aware and honest", watchOut: weakSpot, percentile: "Top 40%", tagline: "Eyes wide open" };
  }
  return { name: "The Seedling", emoji: "🌱", superpower: "The first step is the hardest", watchOut: weakSpot, percentile: "Top 60%", tagline: "Everything ahead of you" };
}

function getWins(surveyData) {
  if (!surveyData) return [];
  const wins = [];
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits } = surveyData;
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  if (bmi && bmi >= 18.5 && bmi < 25) wins.push({ title: `BMI ${bmi.toFixed(1)}`, desc: "Healthy range" });
  if ((!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0)) wins.push({ title: "Smoke-Free", desc: "#1 preventable cause avoided" });
  if (dietHabits === 'no') wins.push({ title: "Clean Diet", desc: "Low processed food intake" });
  if (!alcoholConsumption || alcoholConsumption === "none") wins.push({ title: "Low Alcohol", desc: "Minimal intake" });
  if (wins.length === 0) wins.push({ title: "Taking Action", desc: "You showed up" });
  return wins.slice(0, 3);
}

async function generatePDF(surveyData, recommendations) {
  const jsPDF = await loadJsPDF();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth(); const pageH = doc.internal.pageSize.getHeight(); const margin = 20; let y = margin;
  const checkPage = (n = 20) => { if (y + n > pageH - 20) { doc.addPage(); y = margin; } };
  doc.setFillColor(68, 98, 74); doc.rect(0, 0, pageW, 40, 'F');
  doc.setTextColor(255); doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.text('Pebble Health', margin, 18);
  doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.text('Cancer Screening Report', margin, 27);
  doc.setFontSize(9); doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, 34);
  y = 50;
  const { stronglyRecommend = [], recommend = [], belowAverage = [] } = recommendations || {};
  const contentW = pageW - margin * 2;
  const renderSection = (title, items, color) => { if (!items.length) return; checkPage(30); doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(...color); doc.text(title, margin, y); y += 10; items.forEach(item => { checkPage(20); doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(30); doc.text(item.cancer || 'Screening', margin + 4, y); y += 8; if (item.description) { doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(80); const lines = doc.splitTextToSize(item.description, contentW - 8); doc.text(lines, margin + 4, y); y += lines.length * 4 + 4; } y += 4; doc.setDrawColor(200); doc.line(margin, y, pageW - margin, y); y += 6; }); };
  renderSection('Strongly Recommended', stronglyRecommend, [185, 28, 28]);
  renderSection('Recommended', recommend, [180, 120, 20]);
  renderSection('Routine', belowAverage, [80, 120, 80]);
  const tp = doc.internal.getNumberOfPages();
  for (let i = 1; i <= tp; i++) { doc.setPage(i); doc.setFontSize(7); doc.setTextColor(160); doc.text('Pebble Health — pebblehealth.ai', margin, pageH - 10); doc.text(`Page ${i}/${tp}`, pageW - margin - 15, pageH - 10); }
  doc.save('pebble-health-screening-report.pdf');
}

// ─── Share Modal ───
const ShareModal = ({ isOpen, onClose, imageUrl }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><Sparkles size={18} /> Share</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-6 flex flex-col items-center gap-4">
          {imageUrl ? <img src={imageUrl} alt="Archetype" className="w-64 rounded-xl shadow-lg" /> : <div className="w-64 aspect-[4/5] bg-gray-200 animate-pulse rounded-xl" />}
          <button onClick={() => { const a = document.createElement('a'); a.download = 'pebblehealth-archetype.png'; a.href = imageUrl; a.click(); }} className="w-full py-3 bg-[#44624a] text-white rounded-xl font-bold flex items-center justify-center gap-2"><ImageIcon size={18} /> Save Image</button>
          <button onClick={() => { window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("My health blueprint from Pebble Health ✨")}&url=${encodeURIComponent("https://pebblehealth.ai")}`, '_blank'); }} className="w-full py-3 bg-black text-white rounded-xl font-bold flex items-center justify-center gap-2"><Twitter size={18} /> Share to X</button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ResultPageV4 — "Dashboard / Bento Grid"
// ═══════════════════════════════════════════════════════════════
export default function ResultPageV4() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  const analysisResults = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("omnicare_analysis_results")); } catch { return null; }
  }, []);
  const { surveyData, recommendations } = analysisResults || {};
  const prioritiesData = useMemo(() => {
    const cards = mapRecommendationsToCards(recommendations, surveyData);
    return cards.sort((a, b) => { if (a.isGovRecommended && !b.isGovRecommended) return -1; if (b.isGovRecommended && !a.isGovRecommended) return 1; return parseFloat(b.multiplier) - parseFloat(a.multiplier); });
  }, [recommendations, surveyData]);
  const archetype = useMemo(() => getArchetype(surveyData), [surveyData]);
  const wins = useMemo(() => getWins(surveyData), [surveyData]);

  const handleCaptureCard = async () => {
    if (!cardRef.current) return;
    setIsCapturing(true);
    try {
      const html2canvas = await loadHtml2Canvas();
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, backgroundColor: '#064e3b', logging: false });
      setCapturedImage(canvas.toDataURL('image/png'));
      setShareModalOpen(true);
    } catch (e) { console.error(e); }
    finally { setIsCapturing(false); }
  };

  if (!analysisResults) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-bold">No results found</h2>
          <button onClick={() => navigate('/survey/1')} className="px-6 py-3 bg-[#44624a] text-white rounded-lg font-bold">Take the Quiz</button>
        </div>
      </div>
    );
  }

  const dotColor = { red: 'bg-red-500', orange: 'bg-amber-500', stone: 'bg-gray-400', green: 'bg-emerald-500' };
  const bgColor = { red: 'bg-red-500/10', orange: 'bg-amber-500/10', stone: 'bg-gray-500/10', green: 'bg-emerald-500/10' };

  return (
    <div className="min-h-screen bg-[#F6F6F8] text-gray-900">
      {/* Dark header bar */}
      <header className="bg-[#1A1A2E] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/landing')}>
            <img src={omnicareLogo} alt="Pebble Health" className="w-6 h-6 brightness-0 invert opacity-80" />
            <span className="text-sm font-semibold tracking-tight opacity-90">Pebble Health</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/results')} className="hidden sm:block text-xs text-white/50 hover:text-white/80 transition-colors">Classic View</button>
            <button onClick={handleCaptureCard} className="flex items-center gap-1.5 text-xs font-medium bg-white/10 hover:bg-white/15 px-3 py-1.5 rounded-lg transition-colors">
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {/* ── BENTO GRID ── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-min">

          {/* ── Row 1: Archetype (2col) + Stats (1col each) ── */}
          <div ref={cardRef} className="md:col-span-2 bg-gradient-to-br from-[#44624a] via-[#3a5440] to-[#2f4536] rounded-xl p-4 text-white flex items-center gap-4 min-h-0">
            <span className="text-4xl flex-shrink-0">{archetype.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-white/40">Archetype</p>
              <h2 className="text-xl font-bold tracking-tight truncate">{archetype.name}</h2>
              <p className="text-white/40 text-xs mt-0.5 truncate">{archetype.tagline}</p>
            </div>
            <div className="hidden sm:flex flex-col gap-1.5 flex-shrink-0 max-w-[180px]">
              <div className="bg-white/10 rounded-md px-2.5 py-1.5 border border-white/5">
                <p className="text-[8px] text-[#a3c9a8] uppercase tracking-wider font-bold">Strength</p>
                <p className="text-[11px] font-medium truncate">{archetype.superpower}</p>
              </div>
              <div className="bg-white/10 rounded-md px-2.5 py-1.5 border border-white/5">
                <p className="text-[8px] text-orange-300 uppercase tracking-wider font-bold">Watch</p>
                <p className="text-[11px] font-medium truncate">{archetype.watchOut}</p>
              </div>
            </div>
          </div>

          {/* Count tile */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 flex items-center gap-3">
            <div className="text-3xl font-bold font-mono text-gray-900">{prioritiesData.length}</div>
            <div>
              <p className="text-xs text-gray-500">Screenings</p>
              <div className="flex gap-0.5 mt-1">
                {prioritiesData.map((p, i) => (
                  <div key={i} className={`w-3 h-1.5 rounded-full ${dotColor[p.statusColor]}`} style={{ opacity: 0.7 }} />
                ))}
              </div>
            </div>
          </div>

          {/* Percentile tile */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 flex items-center gap-3">
            <div className="text-2xl font-bold font-mono text-[#44624a]">{archetype.percentile}</div>
            <div>
              <p className="text-xs text-gray-500">of users</p>
              <p className="text-[10px] text-gray-300 mt-0.5">Lifestyle score</p>
            </div>
          </div>

          {/* Wins — compact inline strip */}
          <div className="md:col-span-4 bg-white rounded-xl px-4 py-2.5 border border-gray-200 flex items-center gap-4 flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-gray-400 flex items-center gap-1">
              <CheckCircle2 size={10} className="text-emerald-500" /> Protective
            </span>
            {wins.map((w, i) => (
              <span key={i} className="text-xs text-gray-700 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                <span className="font-semibold">{w.title}</span>
                <span className="text-gray-400 hidden sm:inline">— {w.desc}</span>
              </span>
            ))}
          </div>

          {/* ── Priority tiles: true bento with mixed sizes ── */}
          {/* Top priority = 2col "featured" row, rest = compact 1col grid */}
          {prioritiesData.length > 0 && (
            <>
              {/* Featured first item — wider but still compact */}
              {(() => {
                const data = prioritiesData[0];
                const isExpanded = expandedCard === 0;
                return (
                  <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden">
                    <div className="p-4 flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${data.statusColor === 'red' ? 'bg-red-100 text-red-600' : data.statusColor === 'orange' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                        <data.icon size={16} strokeWidth={2.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-gray-900 truncate">{data.title}</h3>
                          <span className={`text-[10px] font-mono font-bold px-1.5 py-px rounded ${bgColor[data.statusColor]} ${data.statusColor === 'red' ? 'text-red-700' : data.statusColor === 'orange' ? 'text-amber-700' : 'text-gray-500'}`}>{data.multiplier}×</span>
                          {data.isGovRecommended && <span className="text-[8px] font-bold text-blue-600 bg-blue-50 px-1 py-px rounded">GOV</span>}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{data.summary}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[11px] font-semibold text-[#44624a] flex items-center gap-1">{data.action} <ArrowRight size={10} /></span>
                          {data.clinicalText && <button onClick={() => setExpandedCard(isExpanded ? null : 0)} className="text-[10px] text-gray-400 hover:text-gray-600">{isExpanded ? '▲ Less' : '▼ More'}</button>}
                        </div>
                      </div>
                    </div>
                    {isExpanded && data.clinicalText && (
                      <div className="px-4 pb-3 border-t border-gray-100">
                        <div className="mt-2 p-2 bg-gray-50 rounded text-[10px] text-gray-600 whitespace-pre-line font-mono leading-relaxed">{data.clinicalText}</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Remaining items — compact 1-col tiles */}
              {prioritiesData.slice(1).map((data, idx) => {
                const index = idx + 1;
                const isExpanded = expandedCard === index;
                return (
                  <div key={index} className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden">
                    <button onClick={() => setExpandedCard(isExpanded ? null : index)} className="w-full text-left p-3 flex items-center gap-2.5">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor[data.statusColor]}`} />
                      <span className="text-sm font-bold text-gray-900 truncate flex-1">{data.title}</span>
                      <span className={`text-[10px] font-mono font-bold px-1.5 py-px rounded flex-shrink-0 ${bgColor[data.statusColor]} ${data.statusColor === 'red' ? 'text-red-700' : data.statusColor === 'orange' ? 'text-amber-700' : 'text-gray-500'}`}>{data.multiplier}×</span>
                      {data.isGovRecommended && <span className="text-[7px] font-bold text-blue-600 bg-blue-50 px-1 py-px rounded flex-shrink-0">GOV</span>}
                      <ChevronDown size={12} className={`text-gray-300 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mt-2">{data.summary}</p>
                        <span className="text-[10px] font-semibold text-[#44624a] flex items-center gap-1 mt-1.5">{data.action} <ArrowRight size={9} /></span>
                        {data.clinicalText && (
                          <details className="mt-2">
                            <summary className="text-[9px] text-gray-400 cursor-pointer">Clinical details</summary>
                            <div className="mt-1 p-2 bg-gray-50 rounded text-[9px] text-gray-500 whitespace-pre-line font-mono">{data.clinicalText}</div>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {prioritiesData.length === 0 && (
            <div className="md:col-span-4 bg-white rounded-xl border border-gray-200 p-6 text-center">
              <ShieldCheck className="text-emerald-500 mx-auto mb-2" size={32} />
              <h3 className="text-sm font-bold mb-0.5">All Clear</h3>
              <p className="text-gray-500 text-xs">No elevated risk factors detected.</p>
            </div>
          )}

          {/* PDF tile */}
          <div className="md:col-span-4 bg-[#1A1A2E] rounded-xl px-5 py-4 text-white flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FileText size={18} className="text-white/50" />
              <div>
                <h3 className="text-sm font-bold">Full Clinical Report</h3>
                <p className="text-white/40 text-xs">PDF for your doctor</p>
              </div>
            </div>
            <button onClick={() => generatePDF(surveyData, recommendations)} className="px-4 py-2 bg-white text-[#1A1A2E] rounded-lg font-bold text-xs flex items-center gap-1.5 hover:bg-gray-100 transition-colors whitespace-nowrap">
              <Download size={13} /> Download
            </button>
          </div>
        </div>
      </main>

      <div className="sm:hidden text-center py-6">
        <button onClick={() => navigate('/results')} className="text-gray-400 underline text-sm">Classic View →</button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} imageUrl={capturedImage} />
    </div>
  );
}
